/**
 * Momente des Otter-Schranks (Aura v2, Okt 2026): Texte fuer Schrank und Freischaltung.
 *
 * Die Daten (Teil, Platz, Welle, versteckt, Konflikte) stehen im Backend-Katalog
 * (backend/src/constants/otterCatalog.js) und kommen per GET /api/v1/otter. Hier
 * stehen nur die Saetze, die die App zeigt:
 *  - name: Name des Moments ("Startklar")
 *  - part: Name des Teils (wie ACCESSORY_LABELS im Backend), null = Moment ohne Teil
 *  - stage: 'erste_schritte' (sichtbar ab Tag 1, im Schrank als Umriss mit Hinweis),
 *           'gemeinsam' (versteckt) oder 'welle_2' (versteckt, spaeter)
 *  - condition: Bedingungssatz fuer den Umriss im Schrank (nur Erste Schritte)
 *  - feedback: Rueckmeldesatz im Sheet "Neu im Schrank"
 *
 * Teile sind Rueckmeldung auf etwas, das schon passiert ist, kein angekuendigter
 * Lohn: keine Zaehler ("Noch 2 Sessions bis ..."), versteckte Momente erscheinen
 * im Schrank nur als "Noch N Teile versteckt".
 */

export const SLOT_ORDER = ['kopf', 'augen', 'hals', 'brust', 'tasche', 'ohr'];

export const SLOT_LABELS = {
  kopf: 'Kopf',
  augen: 'Augen',
  hals: 'Hals',
  brust: 'Brust',
  tasche: 'Tasche',
  ohr: 'Ohr',
};

export const OTTER_MOMENTS = {
  // Erste Schritte (Welle 1, sichtbar)
  startklar: {
    name: 'Startklar',
    part: 'Beanie',
    stage: 'erste_schritte',
    condition: 'Dein Profil ist fertig: mindestens ein Skill, den du anbietest, und einer, den du suchst.',
    feedback: 'Dein Profil steht, jetzt können dich andere finden.',
  },
  erster_schritt: {
    name: 'Erster Schritt',
    part: null,
    stage: 'erste_schritte',
    condition: 'Du schickst deine erste Vernetzungsanfrage.',
    feedback: 'Deine Anfrage ist raus, jetzt ist die andere Person dran.',
  },
  verbunden: {
    name: 'Verbunden',
    part: 'Sonnenbrille',
    stage: 'erste_schritte',
    condition: 'Deine erste Vernetzungsanfrage wird angenommen. Beide Seiten bekommen die Brille im selben Moment.',
    feedback: 'Ihr seid vernetzt, die Sonnenbrille gibt es für euch beide.',
  },
  zu_zweit: {
    name: 'Zu zweit',
    part: 'Kette',
    stage: 'erste_schritte',
    condition: 'Deine erste echte gemeinsame Session ist geschafft: beide per Code bestätigt, Mindestdauer erreicht.',
    feedback: 'Deine erste Session zu zweit ist geschafft.',
  },
  am_steuer: {
    name: 'Am Steuer',
    part: 'Cap',
    stage: 'erste_schritte',
    condition: 'Du leitest zum ersten Mal eine Session, und mindestens eine andere Person ist bestätigt dabei.',
    feedback: 'Du hast deine erste Session geleitet, und jemand war dabei.',
  },
  im_tunnel: {
    name: 'Im Tunnel',
    part: 'Kopfhörer',
    stage: 'erste_schritte',
    condition: 'Deine erste Fokus-Session läuft bis zum Ende, mindestens 10 Minuten.',
    feedback: 'Deine erste Fokus-Session ist durch.',
  },
  rueckblick: {
    name: 'Rückblick',
    part: 'Runde Brille',
    stage: 'erste_schritte',
    condition: 'Du schickst zum ersten Mal ein Session-Feedback ab.',
    feedback: 'Du hast zum ersten Mal aufgeschrieben, wie eine Session lief.',
  },
  notiert: {
    name: 'Notiert',
    part: 'Bleistift',
    stage: 'erste_schritte',
    condition: 'Dein erster Eintrag im Lernlogbuch.',
    feedback: 'Dein erster Logbucheintrag steht.',
  },
  geschafft: {
    name: 'Geschafft',
    part: 'Hoodie',
    stage: 'erste_schritte',
    condition: 'Du erreichst dein erstes Lernziel.',
    feedback: 'Du hast dein erstes Lernziel erreicht.',
  },

  // Gemeinsam (Welle 1, versteckt)
  crew: {
    name: 'Crew',
    part: 'Bauchtasche',
    stage: 'gemeinsam',
    condition: 'Du hast mit drei verschiedenen Leuten echte Sessions gemacht.',
    feedback: 'Du hast schon mit drei verschiedenen Leuten gelernt.',
  },
  eingespielt: {
    name: 'Eingespielt',
    part: 'Creole',
    stage: 'gemeinsam',
    condition: 'Drei echte Sessions mit derselben Person.',
    feedback: 'Drei Sessions mit derselben Person, ihr seid eingespielt.',
  },

  // Welle 2 (versteckt, kommt spaeter)
  seitenwechsel: {
    name: 'Seitenwechsel',
    part: 'Cap (rückwärts)',
    stage: 'welle_2',
    condition: 'Mit derselben Person hast du einmal eingeladen und wurdest einmal eingeladen.',
    feedback: 'Mal hast du eingeladen, mal die andere Person: Seiten gewechselt.',
  },
  grosse_runde: {
    name: 'Große Runde',
    part: 'Bandana',
    stage: 'welle_2',
    condition: 'Eine Session mit mindestens drei bestätigten Personen.',
    feedback: 'Ihr wart zu dritt oder mehr, das war eine echte Runde.',
  },
  offene_runde: {
    name: 'Offene Runde',
    part: 'Bucket Hat',
    stage: 'welle_2',
    condition: 'Du kommst in eine offene Session dazu, die jemand anderes angelegt hat.',
    feedback: 'Du bist in eine offene Session dazugekommen.',
  },
  eingetaucht: {
    name: 'Eingetaucht',
    part: 'Taucherbrille',
    stage: 'welle_2',
    condition: 'Zehn echte Sessions mit anderen, egal mit wem.',
    feedback: 'Zehn Sessions mit anderen, du bist drin.',
  },
  mitgestaltet: {
    name: 'Mitgestaltet',
    part: 'Rucksack',
    stage: 'welle_2',
    condition: 'Dein Skill-Vorschlag wird angenommen und steht im Katalog deiner Schule.',
    feedback: 'Dein Skill-Vorschlag steht jetzt im Katalog deiner Schule.',
  },
  wieder_da: {
    name: 'Wieder da',
    part: 'Stirnband',
    stage: 'welle_2',
    condition: 'Nach mindestens vier Wochen Pause machst du wieder eine Session.',
    feedback: 'Schön, dass du wieder da bist.',
  },
};

/** Texte zu einem Moment oder null */
export const getMoment = (key) => (
  Object.prototype.hasOwnProperty.call(OTTER_MOMENTS, key) ? OTTER_MOMENTS[key] : null
);

/** Name eines Teils: Server-Label, sonst unsere Konstante, sonst der Schluessel */
export const partLabel = (entry) => {
  if (!entry) return '';
  return entry.label || getMoment(entry.key)?.part || entry.accessory || entry.key;
};
